import React from "react";
import {
  Alert,
  Box,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Field, useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import Div from "@jumbo/shared/Div/Div";
import { Label } from "app/pages/presentation/module/form/types";

const DatavisionPOSTab = ({ readOnly = false }) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();
  const datavisionPosEnabled =
    values?.customer?.datavisionPosEnabled === true;
  const disabled = readOnly;
  const productsPerRowRaw = values?.pos?.productsPerRow;
  const bindingProductsPerRowRaw = values?.productDetail?.bindingProductsPerRow;
  const showBindingsInRow = values?.productDetail?.showBindingsInRow === true;

  const toPositiveIntegerOrEmpty = (value) => {
    const s = String(value ?? "").trim();
    if (s === "") return "";
    const n = Number(s);
    if (!Number.isInteger(n) || n <= 0) return NaN;
    return n;
  };

  const productsPerRowParsed = toPositiveIntegerOrEmpty(productsPerRowRaw);
  const bindingProductsPerRowParsed = toPositiveIntegerOrEmpty(
    bindingProductsPerRowRaw,
  );
  const productsPerRowError =
    productsPerRowRaw !== undefined &&
    productsPerRowRaw !== "" &&
    Number.isNaN(productsPerRowParsed);
  const bindingProductsPerRowError =
    bindingProductsPerRowRaw !== undefined &&
    bindingProductsPerRowRaw !== "" &&
    Number.isNaN(bindingProductsPerRowParsed);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          <Typography variant="body2" component="span" sx={{ display: "block", mb: 1 }}>
            {t("pages.dsokDetails.customerSettingsTabs.datavisionPos.helpBlock")}
          </Typography>
          <Typography variant="body2" component="span">
            {t("pages.dsokDetails.customerSettingsTabs.datavisionPos.helpBlockPrinters")}
          </Typography>
        </Alert>
      </Grid>

      <Grid item xs={12}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          sx={{ mt: 1, mb: 1 }}
        >
          <Div sx={{ mx: 0.5 }}>
            <FormControlLabel
              control={
                <Field
                  name="customer.datavisionPosEnabled"
                  component={Switch}
                  disabled={disabled}
                  onChange={(e) =>
                    setFieldValue(
                      "customer.datavisionPosEnabled",
                      e.target.checked
                    )
                  }
                />
              }
              label={
                <Label
                  text={t(
                    "pages.dsokDetails.customerSettingsTabs.datavisionPos.datavisionPosEnabledLabel",
                    { defaultValue: "Povoliť POS systém (Pokladnica)" },
                  )}
                  helpText={t(
                    "pages.dsokDetails.customerSettingsTabs.datavisionPos.datavisionPosEnabledHelp",
                  )}
                  sx={{ color: "text.primary" }}
                />
              }
              checked={datavisionPosEnabled}
            />
          </Div>
          <Div sx={{ mx: 0.5 }}>
            <FormControlLabel
              control={
                <Field
                  name="customer.pickupDisplayEnabled"
                  component={Switch}
                  disabled={disabled || !datavisionPosEnabled}
                  onChange={(e) =>
                    setFieldValue(
                      "customer.pickupDisplayEnabled",
                      e.target.checked
                    )
                  }
                />
              }
              label={
                <Label
                  text={t(
                    "pages.dsokDetails.customerSettingsTabs.datavisionPos.pickupDisplayEnabledLabel"
                  )}
                  helpText={t(
                    "pages.dsokDetails.customerSettingsTabs.datavisionPos.pickupDisplayEnabledHelp"
                  )}
                  sx={{ color: "text.primary" }}
                />
              }
              checked={values?.customer?.pickupDisplayEnabled === true}
            />
          </Div>
          <Div sx={{ mx: 0.5 }}>
            <FormControlLabel
              control={
                <Field
                  name="customer.kitchenDisplayEnabled"
                  component={Switch}
                  disabled={disabled || !datavisionPosEnabled}
                  onChange={(e) =>
                    setFieldValue(
                      "customer.kitchenDisplayEnabled",
                      e.target.checked
                    )
                  }
                />
              }
              label={
                <Label
                  text={t(
                    "pages.dsokDetails.customerSettingsTabs.datavisionPos.kitchenDisplayEnabledLabel"
                  )}
                  helpText={t(
                    "pages.dsokDetails.customerSettingsTabs.datavisionPos.kitchenDisplayEnabledHelp"
                  )}
                  sx={{ color: "text.primary" }}
                />
              }
              checked={values?.customer?.kitchenDisplayEnabled === true}
            />
          </Div>
          <Div sx={{ mx: 0.5 }}>
            <FormControlLabel
              control={
                <Field
                  name="customer.kitchenPrintingEnabled"
                  component={Switch}
                  disabled={disabled || !datavisionPosEnabled}
                  onChange={(e) =>
                    setFieldValue(
                      "customer.kitchenPrintingEnabled",
                      e.target.checked
                    )
                  }
                />
              }
              label={
                <Label
                  text={t(
                    "pages.dsokDetails.customerSettingsTabs.datavisionPos.kitchenPrintingEnabledLabel"
                  )}
                  helpText={t(
                    "pages.dsokDetails.customerSettingsTabs.datavisionPos.kitchenPrintingEnabledHelp"
                  )}
                  sx={{ color: "text.primary" }}
                />
              }
              checked={values?.customer?.kitchenPrintingEnabled === true}
            />
          </Div>
        </Stack>
      </Grid>

      {!datavisionPosEnabled && (
        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t(
              "pages.dsokDetails.customerSettingsTabs.datavisionPos.enableMasterNote"
            )}
          </Typography>
        </Grid>
      )}
      {datavisionPosEnabled && (
        <Grid item xs={12}>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {t(
                "pages.dsokDetails.customerSettingsTabs.datavisionPos.posSettingsTitle",
                { defaultValue: "Nastavenie POS" },
              )}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4} lg={3}>
                <TextField
                  fullWidth
                  type="number"
                  label={t(
                    "pages.dsokDetails.customerSettingsTabs.datavisionPos.productsPerRowLabel",
                    { defaultValue: "Počet produktov v riadku" },
                  )}
                  value={productsPerRowRaw ?? ""}
                  onChange={(e) =>
                    setFieldValue("pos.productsPerRow", e.target.value)
                  }
                  disabled={disabled}
                  inputProps={{ min: 1, step: 1 }}
                  error={productsPerRowError}
                  helperText={
                    productsPerRowError
                      ? t("messages.checkRequiredFields")
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={12} md={4} lg={3}>
                <TextField
                  fullWidth
                  type="number"
                  label={t(
                    "pages.dsokDetails.customerSettingsTabs.datavisionPos.bindingProductsPerRowLabel",
                    { defaultValue: "Počet binding produktov v riadku" },
                  )}
                  value={bindingProductsPerRowRaw ?? ""}
                  onChange={(e) =>
                    setFieldValue(
                      "productDetail.bindingProductsPerRow",
                      e.target.value,
                    )
                  }
                  disabled={disabled}
                  inputProps={{ min: 1, step: 1 }}
                  error={bindingProductsPerRowError}
                  helperText={
                    bindingProductsPerRowError
                      ? t("messages.checkRequiredFields")
                      : ""
                  }
                />
              </Grid>
              <Grid item xs={12} md={4} lg={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showBindingsInRow}
                      onChange={(e) =>
                        setFieldValue(
                          "productDetail.showBindingsInRow",
                          e.target.checked,
                        )
                      }
                      disabled={disabled}
                    />
                  }
                  label={t(
                    "pages.dsokDetails.customerSettingsTabs.datavisionPos.showBindingsInRowLabel",
                    { defaultValue: "Zobraziť bindings v riadku" },
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default DatavisionPOSTab;
